/**
 * Shopping lists index — DESIGN_GUIDELINES.md §9.3.
 */

import { Link } from 'react-router-dom';
import { Alert, Anchor, Button, Card, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { PageShell } from '../components/PageShell';
import { deleteList, fetchLists, type SessionUser } from '../lib/api';
import { useAsync } from '../lib/useAsync';

export function ListsPage({ user, onSignOut }: { user: SessionUser; onSignOut: () => void }) {
  const lists = useAsync(() => fetchLists(), []);

  async function handleDelete(id: string) {
    await deleteList(id);
    lists.reload();
  }

  return (
    <PageShell
      user={user}
      onSignOut={onSignOut}
      title="Shopping lists"
      actions={
        <Button component={Link} to="/lists/new" radius="xl">
          New list
        </Button>
      }
    >
      {lists.error ? (
        <Alert color="red" variant="light" role="alert">
          {lists.error}
        </Alert>
      ) : null}

      {lists.loading ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : null}

      {lists.data && lists.data.length === 0 ? (
        <Stack align="center" gap="sm" py="xl">
          <Title order={3}>No lists yet</Title>
          <Text size="sm" c="var(--ff-muted)" ta="center" maw={420}>
            Pick a few recipes and the planner will combine their ingredients into one shopping
            list, adding up the quantities for you.
          </Text>
          <Button component={Link} to="/lists/new" radius="xl" mt="xs">
            Build a list
          </Button>
        </Stack>
      ) : null}

      {lists.data && lists.data.length > 0 ? (
        <Stack gap="sm">
          {lists.data.map((list) => (
            <Card key={list.id} withBorder radius="lg" padding="md">
              <Group justify="space-between" wrap="nowrap">
                <Anchor component={Link} to={`/lists/${list.id}`} fw={600} c="var(--ff-ink)" td="none">
                  {list.name}
                </Anchor>
                <Button
                  variant="subtle"
                  color="gray"
                  size="compact-sm"
                  onClick={() => void handleDelete(list.id)}
                >
                  Delete
                </Button>
              </Group>
            </Card>
          ))}
        </Stack>
      ) : null}
    </PageShell>
  );
}
